import React, { useState, useEffect, useCallback } from 'react';
import BoissonPill from './BoissonPill';

const CATEGORIES = [
  { key: 'alcool', label: 'Alcools', icon: '🥃' },
  { key: 'biere',  label: 'Bières',  icon: '🍺' },
  { key: 'eau',   label: 'Eaux',    icon: '💧' },
  { key: 'soda',  label: 'Sodas',   icon: '🥤' },
  { key: 'jus',   label: 'Jus',     icon: '🍹' },
  { key: 'soft',  label: 'Divers',  icon: '🍯' },
];

export default function BoissonsList({ participant, initialChoix, onConfirm, onBack }) {
  const [boissons, setBoissons] = useState([]);
  const [mesQuantites, setMesQuantites] = useState({ ...initialChoix });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchBoissons = useCallback(async () => {
    try {
      const res = await fetch('/api/boissons');
      const data = await res.json();
      setBoissons(data);
    } catch {
      setError('Impossible de charger les boissons.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBoissons(); }, [fetchBoissons]);

  const increment = async (boissonId) => {
    setSaving(true);
    try {
      const res = await fetch('/api/participation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant_id: participant.id, boisson_id: boissonId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMesQuantites(prev => ({ ...prev, [boissonId]: (prev[boissonId] || 0) + 1 }));
      await fetchBoissons();
    } catch (err) {
      setError(err.message || 'Erreur, réessaie.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const decrement = async (boissonId) => {
    setSaving(true);
    try {
      const res = await fetch('/api/participation', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant_id: participant.id, boisson_id: boissonId })
      });
      if (!res.ok) throw new Error();
      setMesQuantites(prev => {
        const next = { ...prev };
        const newVal = (next[boissonId] || 0) - 1;
        if (newVal <= 0) delete next[boissonId];
        else next[boissonId] = newVal;
        return next;
      });
      await fetchBoissons();
    } catch {
      setError('Erreur, réessaie.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const byCategory = CATEGORIES.map(cat => ({
    ...cat,
    items: boissons.filter(b => b.categorie === cat.key)
  })).filter(cat => cat.items.length > 0);

  const totalUnites = Object.values(mesQuantites).reduce((sum, q) => sum + q, 0);

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '5rem' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #00acc1, #007c91)', padding: '1.2rem 1rem 1rem', color: '#fff' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontWeight: 700, padding: '0 0 0.75rem', cursor: 'pointer', fontSize: '0.9rem' }}>
          <i className="bi bi-arrow-left me-1" />Retour
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>
            👤
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.15rem' }}>Salut {participant.nom} !</div>
            <div style={{ fontSize: '0.82rem', opacity: 0.85 }}>
              Touche la partie <strong>claire</strong> pour ajouter, la partie <strong>foncée</strong> pour retirer
            </div>
          </div>
        </div>
      </div>

      <div className="container px-3" style={{ maxWidth: 480, paddingTop: '1rem' }}>
        {error && (
          <div className="alert alert-warning py-2 px-3 rounded-pill text-center mb-3" style={{ fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: 'var(--party-primary)' }} />
            <p className="mt-2" style={{ color: '#607d8b' }}>Chargement...</p>
          </div>
        ) : (
          <>
            {byCategory.map(cat => (
              <div key={cat.key}>
                <div className="category-label">{cat.icon} {cat.label}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  {cat.items.map(b => (
                    <BoissonPill
                      key={b.id}
                      boisson={b}
                      maQuantite={mesQuantites[b.id] || 0}
                      onIncrement={increment}
                      onDecrement={decrement}
                      disabled={saving}
                    />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Footer sticky */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10, background: '#fff', borderTop: '1px solid #e0f7fa', padding: '0.85rem 1rem', boxShadow: '0 -4px 20px rgba(0,172,193,0.12)' }}>
        <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            {totalUnites === 0 ? (
              <span style={{ color: '#90a4ae', fontSize: '0.88rem' }}>Aucune boisson sélectionnée</span>
            ) : (
              <span style={{ color: 'var(--party-primary)', fontWeight: 700, fontSize: '0.95rem' }}>
                <i className="bi bi-check-circle-fill me-1" />{totalUnites} unité{totalUnites > 1 ? 's' : ''} choisie{totalUnites > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <button
            className="btn btn-party"
            disabled={totalUnites === 0 || saving}
            onClick={() => onConfirm(mesQuantites)}
            style={{ whiteSpace: 'nowrap' }}
          >
            Confirmer <i className="bi bi-arrow-right ms-1" />
          </button>
        </div>
      </div>
    </div>
  );
}
